"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
/**
 * Araç kataloğu (111 araç + anahtar kelimeler, derlenmiş hâlde ~104 KB)
 * İLK ETKİLEŞİMDE yüklenir. Statik içe aktarıldığında, arama hiç
 * kullanılmasa bile her sayfaya gidiyordu.
 */
type Katalog = typeof import("@/lib/tools");
let katalogSoz: Promise<Katalog> | null = null;
const katalogGetir = () => (katalogSoz ??= import("@/lib/tools"));

/** Türkçe arama: i/ı, ş/s gibi farkları yok sayarak eşleştirir. */
function normalle(s: string) {
  return s
    .toLocaleLowerCase("tr-TR")
    .replaceAll("ı", "i")
    .replaceAll("ş", "s")
    .replaceAll("ğ", "g")
    .replaceAll("ü", "u")
    .replaceAll("ö", "o")
    .replaceAll("ç", "c");
}

export function AracAra({ hero = false, buton = false }: { hero?: boolean; buton?: boolean }) {
  const [katalog, setKatalog] = useState<Katalog | null>(null);
  /** Kullanıcı aramaya ilk dokunduğunda kataloğu indirir. */
  const katalogHazirla = () => {
    if (!katalog) katalogGetir().then(setKatalog);
  };
  const [q, setQ] = useState("");
  const [acik, setAcik] = useState(false);
  const [vurgu, setVurgu] = useState(0);
  const [panelAcik, setPanelAcik] = useState(false);
  const kutu = useRef<HTMLDivElement>(null);
  const girdiRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  const sonuclar = useMemo(() => {
    if (!katalog) return [];
    // Çok kelimeli arama: sorguyu kelimelere böl, hepsi eşleşen araçları getir
    const kelimeler = normalle(q.trim()).split(/\s+/).filter(Boolean);
    if (!kelimeler.length) return [];
    return katalog.tools
      .filter((t) => {
        const hedef = normalle(t.name + " " + t.blurb + " " + t.keywords.join(" "));
        return kelimeler.every((k) => hedef.includes(k));
      })
      .slice(0, 6)
      .map((t) => ({ ...t, renkSinifi: katalog.categories[t.category].text }));
  }, [q, katalog]);

  useEffect(() => {
    const dis = (e: MouseEvent) => {
      if (kutu.current && !kutu.current.contains(e.target as Node)) {
        setAcik(false);
        setPanelAcik(false);
        setQ("");
      }
    };
    document.addEventListener("mousedown", dis);
    return () => document.removeEventListener("mousedown", dis);
  }, []);

  // Buton modunda panel açılınca arama alanına odaklan
  useEffect(() => {
    if (panelAcik) girdiRef.current?.focus();
  }, [panelAcik]);

  /** Buton modunda paneli kapat — eski sorgu kalmasın, tekrar açılınca temiz başlasın. */
  const paneliKapat = () => {
    setPanelAcik(false);
    setQ("");
    setVurgu(0);
  };

  function git(slug: string) {
    setQ("");
    setAcik(false);
    setPanelAcik(false);
    router.push(`/${slug}`);
  }

  // ---- Buton modu: yalnızca ikon; tıklayınca arama paneli açılır ----
  if (buton) {
    return (
      <div ref={kutu} className="relative">
        <button
          type="button"
          onClick={() => {
            katalogHazirla();
            setPanelAcik((v) => !v);
          }}
          aria-label="Araç ara"
          aria-expanded={panelAcik}
          className={`grid size-9 place-items-center rounded-lg border transition ${
            panelAcik ? "border-brand text-brand" : "border-border text-muted hover:border-brand/50 hover:text-foreground"
          }`}
        >
          <Search className="size-4" />
        </button>

        {/*
          Mobilde EKRANA SABİT: panel düğmeye right-0 ile yaslandığında,
          düğme sağda olmadığı için (sağında tema ve menü var) 352px'lik
          panel ekranın soluna 66px taşıyordu. sm ve üstünde düğmeye
          yaslanma korunuyor.
        */}
        {panelAcik && (
          <div className="fixed inset-x-3 top-[68px] z-50 overflow-hidden rounded-xl border border-border bg-surface shadow-[var(--shadow-lg)] sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[min(22rem,calc(100vw-2rem))]">
            <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
              <Search className="size-4 shrink-0 text-muted" />
              <input
                ref={girdiRef}
                value={q}
                onChange={(e) => {
                  katalogHazirla();
            setQ(e.target.value);
                  setVurgu(0);
                  setAcik(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    paneliKapat();
                    return;
                  }
                  if (!sonuclar.length) return;
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setVurgu((v) => (v + 1) % sonuclar.length);
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setVurgu((v) => (v - 1 + sonuclar.length) % sonuclar.length);
                  } else if (e.key === "Enter") {
                    git(sonuclar[vurgu].slug);
                  }
                }}
                placeholder="Araç ara…"
                aria-label="Araç ara"
                className="w-full bg-transparent text-sm outline-none placeholder:text-muted"
              />
              <button onClick={paneliKapat} aria-label="Kapat" className="text-muted hover:text-foreground">
                <X className="size-3.5" />
              </button>
            </div>

            {q.trim() ? (
              sonuclar.length ? (
                sonuclar.map((t, i) => (
                  <Link
                    key={t.slug}
                    href={`/${t.slug}`}
                    onClick={() => git(t.slug)}
                    className={`flex items-center gap-3 px-3 py-2.5 transition ${i === vurgu ? "bg-surface-muted" : "hover:bg-surface-muted"}`}
                  >
                    <t.icon className={`size-4 shrink-0 ${t.renkSinifi}`} />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{t.name}</span>
                      <span className="block truncate text-xs text-muted">{t.blurb}</span>
                    </span>
                  </Link>
                ))
              ) : (
                <p className="px-3 py-3 text-sm text-muted">{katalog ? "Sonuç bulunamadı." : "Araçlar yükleniyor…"}</p>
              )
            ) : (
              <p className="px-3 py-3 text-xs text-muted">Araç adı yazın — örn. “pdf birleştir”, “video kırp”.</p>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={kutu} className={hero ? "relative mx-auto w-full max-w-2xl" : "relative w-full max-w-xs"}>
      <div
        className={
          hero
            ? "flex h-[60px] items-center gap-2 rounded-2xl border border-border bg-surface p-2 pl-4 transition duration-200 focus-within:border-brand focus-within:ring-4 focus-within:ring-brand/20"
            : "flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-1.5"
        }
      >
        <Search className={hero ? "size-5 shrink-0 text-muted" : "size-4 shrink-0 text-muted"} />
        <input
          value={q}
          onChange={(e) => {
            katalogHazirla();
            setQ(e.target.value);
            setVurgu(0);
            setAcik(true);
          }}
          onFocus={() => {
            katalogHazirla();
            setAcik(true);
          }}
          onKeyDown={(e) => {
            if (!sonuclar.length) return;
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setVurgu((v) => (v + 1) % sonuclar.length);
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setVurgu((v) => (v - 1 + sonuclar.length) % sonuclar.length);
            } else if (e.key === "Enter") {
              git(sonuclar[vurgu].slug);
            } else if (e.key === "Escape") {
              setAcik(false);
            }
          }}
          placeholder={hero ? "Ne yapmak istiyorsunuz?" : "Araç ara…"}
          aria-label="Araç ara"
          className={
            hero
              ? "w-full bg-transparent text-base outline-none placeholder:text-muted"
              : "w-full bg-transparent text-sm outline-none placeholder:text-muted"
          }
        />
        {q && !hero && (
          <button onClick={() => setQ("")} aria-label="Temizle" className="text-muted hover:text-foreground">
            <X className="size-3.5" />
          </button>
        )}
        {hero && (
          <button
            type="button"
            onClick={() => sonuclar.length && git(sonuclar[vurgu]?.slug ?? sonuclar[0].slug)}
            className="h-full shrink-0 rounded-xl bg-gradient-to-br from-brand to-brand-strong px-6 text-sm font-semibold text-brand-fg transition duration-200 hover:brightness-110 active:scale-[0.98]"
          >
            Ara
          </button>
        )}
      </div>

      {acik && q.trim() && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-xl border border-border bg-surface shadow-lg shadow-black/5">
          {sonuclar.length ? (
            sonuclar.map((t, i) => (
              <Link
                key={t.slug}
                href={`/${t.slug}`}
                onClick={() => git(t.slug)}
                className={`flex items-center gap-3 px-3 py-2.5 transition ${i === vurgu ? "bg-surface-muted" : "hover:bg-surface-muted"}`}
              >
                <t.icon className={`size-4 shrink-0 ${t.renkSinifi}`} />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">{t.name}</span>
                  <span className="block truncate text-xs text-muted">{t.blurb}</span>
                </span>
              </Link>
            ))
          ) : (
            <p className="px-3 py-3 text-sm text-muted">{katalog ? "Sonuç bulunamadı." : "Araçlar yükleniyor…"}</p>
          )}
        </div>
      )}
    </div>
  );
}
